import { useState } from 'react';

import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  PhoneIncoming,
  PhoneOutgoing,
  Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCall } from '@/hooks/useCalls';
import { cn } from '@/lib/utils';

import type { TimelineCallItem } from '@repo/shared-types';

const formatDuration = (seconds: number | null): string => {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

type TranscriptMessage = { role: string; content: string };

const extractTranscript = (transcript: unknown): TranscriptMessage[] => {
  if (!transcript || typeof transcript !== 'object') return [];
  const messages = (transcript as { messages?: unknown }).messages;
  if (!Array.isArray(messages)) return [];
  return messages.filter(
    (m): m is TranscriptMessage =>
      !!m && typeof m === 'object' && typeof (m as TranscriptMessage).content === 'string',
  );
};

/**
 * A call rendered inline in the contact thread: a tinted container with a
 * slim header and the transcript as chat bubbles in the same orientation as
 * the message thread (caller left, agent right). The full transcript shows
 * by default — no inner scrollbar; a chevron collapses the block if needed.
 */
export const PhoneSessionBlock = ({
  item,
  onDelete,
  compact = false,
  dark = false,
}: {
  item: TimelineCallItem;
  onDelete?: (callId: string) => void;
  /** Tighter spacing/typography for the multi-chat grid. */
  compact?: boolean;
  /** WhatsApp-dark palette used by the multi-chat grid cards. */
  dark?: boolean;
}) => {
  const [expanded, setExpanded] = useState(true);
  // Transcript is intentionally not in the timeline payload; fetch per call.
  const { data: call, isLoading } = useCall(expanded ? item.id : undefined);
  const transcript = extractTranscript(call?.transcript);

  const inbound = item.direction === 'INBOUND';
  const DirectionIcon = inbound ? PhoneIncoming : PhoneOutgoing;
  const time = new Date(item.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className={cn(
        'group w-full overflow-hidden rounded-xl border',
        dark
          ? 'border-sky-500/20 bg-sky-500/[0.04] dark:border-sky-400/15 dark:bg-sky-400/[0.04]'
          : 'border-sky-500/20 bg-sky-500/[0.04]',
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'flex items-center gap-2 border-b px-2.5 py-1.5',
          dark ? 'border-sky-500/10 dark:border-sky-400/10' : 'border-sky-500/10',
        )}
      >
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full',
            compact ? 'h-5 w-5' : 'h-6 w-6',
            inbound ? 'bg-emerald-500/10 text-emerald-500' : 'bg-sky-500/10 text-sky-500',
          )}
        >
          <DirectionIcon className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span
            className={cn(
              'font-semibold tracking-wide uppercase',
              compact ? 'text-[10px]' : 'text-[11px]',
              dark ? 'text-[#54656f] dark:text-[#aebac1]' : 'text-foreground/80',
            )}
          >
            Phone · {inbound ? 'Inbound' : 'Outbound'}
          </span>
          {item.duration != null && (
            <span
              className={cn(
                'tabular-nums',
                compact ? 'text-[10px]' : 'text-[11px]',
                dark ? 'text-[#667781] dark:text-[#8696a0]' : 'text-muted-foreground',
              )}
            >
              {formatDuration(item.duration)}
            </span>
          )}
          {item.outcome && (
            <Badge variant="secondary" className="px-1.5 py-0 text-[9px] font-medium">
              {item.outcome.replace(/_/g, ' ').toLowerCase()}
            </Badge>
          )}
          {!compact && item.campaign_name && (
            <span
              className={cn(
                'truncate text-[11px]',
                dark ? 'text-[#667781] dark:text-[#8696a0]' : 'text-muted-foreground',
              )}
            >
              {item.campaign_name}
            </span>
          )}
        </div>
        <span
          className={cn(
            'shrink-0 tabular-nums',
            compact ? 'text-[9px]' : 'text-[10px]',
            dark ? 'text-[#667781] dark:text-[#8696a0]' : 'text-muted-foreground',
          )}
        >
          {time}
        </span>
        <div className="flex shrink-0 items-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className={cn(
              'h-6 w-6',
              dark
                ? 'text-[#667781] hover:bg-[#d9dbdf] hover:text-[#111b21] dark:text-[#8696a0] dark:hover:bg-[#2a3942] dark:hover:text-[#e9edef]'
                : 'text-muted-foreground hover:text-foreground',
            )}
            title="Open call"
          >
            <Link to={`/calls/${item.id}`}>
              <ExternalLink className="h-3 w-3" />
            </Link>
          </Button>
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-6 w-6',
                dark
                  ? 'text-[#667781] hover:bg-[#d9dbdf] hover:text-red-500 dark:text-[#8696a0] dark:hover:bg-[#2a3942] dark:hover:text-red-400'
                  : 'text-muted-foreground hover:text-destructive',
              )}
              title="Delete call"
              onClick={() => onDelete(item.id)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'h-6 w-6 shrink-0',
            dark
              ? 'text-[#667781] hover:bg-[#d9dbdf] hover:text-[#111b21] dark:text-[#8696a0] dark:hover:bg-[#2a3942] dark:hover:text-[#e9edef]'
              : 'text-muted-foreground hover:text-foreground',
          )}
          title={expanded ? 'Collapse call' : 'Show call'}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </div>

      {/* Full transcript as chat bubbles — no inner scrollbar */}
      {expanded && (
        <div className="space-y-1 px-2.5 py-2">
          {isLoading ? (
            <div className="flex justify-center py-2">
              <Loader2
                className={cn(
                  'h-4 w-4 animate-spin',
                  dark ? 'text-[#667781] dark:text-[#8696a0]' : 'text-muted-foreground',
                )}
              />
            </div>
          ) : transcript.length === 0 ? (
            <p
              className={cn(
                'py-1 text-center',
                compact ? 'text-[10px]' : 'text-xs',
                dark ? 'text-[#667781] dark:text-[#8696a0]' : 'text-muted-foreground',
              )}
            >
              No transcript available.
            </p>
          ) : (
            transcript.map((msg, i) => {
              // Same orientation as the message thread: contact left, agent right.
              const isAgent = msg.role !== 'user';
              return (
                <div key={i} className={cn('flex', isAgent ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cn(
                      'max-w-[85%] rounded-2xl break-words whitespace-pre-wrap',
                      compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
                      isAgent
                        ? dark
                          ? 'rounded-br-md bg-[#d9fdd3] text-[#111b21] dark:bg-[#005c4b] dark:text-[#e9edef]'
                          : 'bg-primary text-primary-foreground rounded-br-md'
                        : dark
                          ? 'rounded-bl-md bg-white text-[#111b21] dark:bg-[#202c33] dark:text-[#e9edef]'
                          : 'bg-muted rounded-bl-md',
                    )}
                  >
                    {msg.content}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
